<template>
  <Terminal
    :welcomeMessage="welcomeMessage"
    :prompt="prompt"
    :class="[
      className,
      `p-terminal-${size}`,
      {
        'p-terminal-responsive': responsive,
        'p-terminal-rtl': dir === 'rtl'
      }
    ]"
    :style="[{ height }, style]"
    :role="'application'"
    :aria-label="ariaLabel"
    :aria-live="'polite'"
    :tabindex="0"
    @command="onCommand"
    @response="onResponse"
    :autoFocus="autoFocus"
  >
    <template #prompt>
      <span :class="{ 'p-terminal-prompt-rtl': dir === 'rtl' }">{{ prompt }}</span>
    </template>
  </Terminal>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import Terminal from 'primevue/terminal'
import type { ONETerminalProps } from './types'

export default defineComponent({
  name: 'ONETerminal',
  components: {
    Terminal
  },
  inheritAttrs: false,
  emits: ['command', 'response']
})
</script>

<script setup lang="ts">
withDefaults(defineProps<ONETerminalProps>(), {
  welcomeMessage: undefined,
  prompt: '$',
  className: undefined,
  style: undefined,
  height: '300px',
  autoFocus: false,
  responsive: true,
  dir: 'ltr',
  size: 'normal',
  ariaLabel: 'Terminal'
})

const emit = defineEmits<{
  (e: 'command', command: string): void
  (e: 'response', response: string): void
}>()

const onCommand = (command: string) => {
  emit('command', command)
}

const onResponse = (response: string) => {
  emit('response', response)
}
</script>

<style lang="scss">
.p-terminal {
  height: 100%;
  overflow: auto;
  padding: 1rem;
  background: var(--surface-900, var(--one-ink));
  color: var(--surface-0, #ffffff);
  font-family: monospace;

  &-prompt {
    margin-right: 0.5rem;
    user-select: none;

    &-rtl {
      margin-right: 0;
      margin-left: 0.5rem;
    }
  }

  &-command-input {
    background: transparent;
    border: none;
    color: inherit;
    font-family: inherit;
    font-size: inherit;
    width: 75%;

    &:focus {
      outline: none;
    }
  }

  &-small {
    font-size: 0.875rem;
    .p-terminal-command-input {
      font-size: 0.875rem;
    }
  }

  &-normal {
    font-size: 1rem;
    .p-terminal-command-input {
      font-size: 1rem;
    }
  }

  &-large {
    font-size: 1.25rem;
    .p-terminal-command-input {
      font-size: 1.25rem;
    }
  }

  &-responsive {
    @media screen and (max-width: 576px) {
      font-size: 0.875rem;
      .p-terminal-command-input {
        font-size: 0.875rem;
        width: 60%;
      }
    }
  }

  &-rtl {
    direction: rtl;
    .p-terminal-content {
      text-align: right;
    }
    .p-terminal-command {
      flex-direction: row-reverse;
    }
    .p-terminal-command-input {
      text-align: right;
    }
  }

  // Accessibility - High contrast mode
  @media screen and (-ms-high-contrast: active) {
    border: 1px solid currentColor;
  }

  // Focus styles
  &:focus {
    outline: none;
    box-shadow: 0 0 0 0.2rem rgba(33, 150, 243, 0.25);
  }

  // Theme integration
  background: var(--terminal-bg, var(--surface-900, var(--one-ink)));
  color: var(--terminal-color, var(--surface-0, #ffffff));
  border: var(--terminal-border, 1px solid var(--surface-700, var(--one-ink-soft)));
}
</style>
